import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Icon from '../../../components/_base/Icon';
import DefaultSpinner from '../../shared/DefaultSpinner';
import { useEventPublicInfo } from '../../../hooks/api/public/use-event-public-details';
import { useCalendarInviteSubscription } from '../../../hooks/use-calendar-subscription';
import { getUrlQueryArgByKey } from '../../../lib/api/utils';
import { ML_ACCESS_TOKEN, SUBSCRIBE_TO_EVENT } from '../../../lib/constants';
import { preRegisterToCalendarInvite, subscribeToCalendarInvite } from '../../../lib/api/protected';
import { RegistrationSubscriptionParams } from '../../../lib/api';
import './CalendarServicePage.scss';
import { ROOT, WELCOME_PATH } from '../../../config/routes/paths';
import { usePathPrefix } from '../../../hooks/use-path-prefix';
import { normalizeRoutePath } from '../../../lib/helpers/urlHelper';

export const CalendarServicePage: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState(false);

  const history = useHistory();
  const token = getUrlQueryArgByKey(ML_ACCESS_TOKEN) || '';
  const { data } = useEventPublicInfo();
  const { state, subscribe } = useCalendarInviteSubscription();
  const pathPrefix = usePathPrefix();
  const normalizedEmptyRoute = normalizeRoutePath(ROOT, pathPrefix);

  const closeModal = () => {
    if (history.location.pathname.endsWith(WELCOME_PATH) && state.subscribed) {
      history.replace({
        pathname: normalizedEmptyRoute
      });
    }
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeModal();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (state.subscribed !== null) {
      setLoading(false);
      setToastMessage(state.subscribed);
    }
  }, [state]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!state.inProgress && params.get(SUBSCRIBE_TO_EVENT) === 'true') {
      if (!token) return handleError('Something went wrong. Please try again or contact support.');

      setIsOpen(true);
      const email = extractEmailFromToken(token);
      if (!email) return;
      sendInvite(email);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, state]);

  const handleError = (message: string) => {
    setIsOpen(true);
    setError(message);
    setLoading(false);
  };

  const extractEmailFromToken = (jwt: string): string | null => {
    try {
      const [headerB64] = jwt.split('.');
      const header = JSON.parse(atob(headerB64));
      const email = header?.content?.email;

      if (!email) throw new Error('Email not found in token');
      return email;
    } catch {
      handleError('Invalid token format');
      return null;
    }
  };

  const sendInvite = async (emailId: string) => {
    if (!emailId || !data?.eventId) return;

    try {
      const response = await preRegisterToCalendarInvite({
        attendeeEmail: emailId,
        eventId: data.eventId.toString(),
      });

      await handleSubscription(response);
    } catch {
      handleError('Something went wrong. Please try again or contact support.');
    } finally {
      resetUrlParams();
    }
  };

  const handleSubscription = async (params: RegistrationSubscriptionParams) => {
    try {
      await subscribe({
        registrationId: params.registrationId,
        appSyncToken: params.appSyncToken,
      });
      await subscribeToCalendarInvite(params.registrationId);
    } catch {
      handleError('Something went wrong. Please try again or contact support.');
    }
  };

  const resetUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete(ML_ACCESS_TOKEN);
    params.delete(SUBSCRIBE_TO_EVENT);

    history.replace({ search: params.toString() });
  };

  const renderContent = () => {
    if (loading) return <div className="h-32 content-center"><DefaultSpinner visible /></div>;
    if (toastMessage) {
      return (
        <div className="h-36 overflow-auto">
          <div className="mb-5">
            <Icon.CheckedCalendar width="35" height="35" viewBox="0 0 50 50" className="col order-first mr-2" />
          </div>
          <p className="text-sm">Thanks. We’ve sent you a calendar invite for</p>
          <p className="font-bold text-sm mb-6 overflow-auto">
            {data?.eventTitleForPassport || data?.name}.
          </p>
          <p className="text-sm">We will update your invite if anything changes.</p>
        </div>
      );
    }
    if (error) return <div className="mt-24 h-12 text-red-500 font-medium text-center">{error}</div>;

    return null;
  };

  return (
    <>
      {isOpen && (
        <div className="main-box inset-0 bg-gray-100">
          <div className="modal-container">
            <button className="cross-icon" onClick={closeModal}>X</button>
            <div className="mt-10 w-full">{renderContent()}</div>
            <button className="mt-10 close-button" onClick={closeModal}>
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};
